import { NavLink } from 'react-router-dom';
import {
  FileText,
  Home as HomeIcon,
  Settings,
  ShoppingBag,
  ShoppingCart,
  type LucideIcon,
} from 'lucide-react';

import { cn } from '@/lib/utils';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

const ITEMS: NavItem[] = [
  { to: '/home',     label: 'Home',     icon: HomeIcon },
  { to: '/order',    label: 'Shop',     icon: ShoppingBag },
  { to: '/cart',     label: 'Cart',     icon: ShoppingCart },
  { to: '/invoices', label: 'Billing',  icon: FileText },
  { to: '/settings', label: 'Settings', icon: Settings },
];

/**
 * Bottom navigation — visible on mobile only.
 *
 * Designed for thumbs: 56px tall, 5 equal touch targets, brand-primary top
 * indicator on the active route. Frosted-glass surface so the brand wash
 * shows through.
 */
export function BottomNav() {
  return (
    <nav
      data-app-chrome
      className={cn(
        'md:hidden fixed bottom-0 left-0 right-0 z-40 h-14',
        'border-t border-brix-border bg-brix-surface/85 backdrop-blur-xl',
      )}
      aria-label="Primary"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <ul className="grid grid-cols-5 h-full">
        {ITEMS.map(({ to, label, icon: Icon }) => (
          <li key={to} className="flex">
            <NavLink
              to={to}
              className={({ isActive }) =>
                cn(
                  'relative flex-1 flex flex-col items-center justify-center gap-0.5',
                  'text-[10px] transition-colors duration-200 cursor-pointer',
                  isActive ? 'text-brix-primary font-semibold' : 'text-brix-muted',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    aria-hidden
                    className={cn(
                      'absolute top-0 left-1/2 -translate-x-1/2 h-0.5 rounded-b-full bg-brix-primary transition-all duration-200',
                      isActive ? 'w-8 opacity-100' : 'w-0 opacity-0',
                    )}
                  />
                  <Icon
                    size={20}
                    strokeWidth={isActive ? 2 : 1.75}
                    className="transition-all duration-200"
                  />
                  {label}
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
